define((require) => {
    "use strict";

    const ImprovedModel                   = require("shared/zbone/ImprovedModel");
    const Enum                            = require("shared/util/Enum");
    const {each, extend, isString}        = require("underscore");
    const DashboardSorting                = require("ui/dashboard/DashboardSorting");
    const LoggedIn                        = require("shared/security/LoggedIn");
    const LoggedInUserState               = require("shared/ui/user/LoggedInUserState");
    const ImageCollection                 = require("data/ImageCollection");
    const ChannelCollection               = require("data/ChannelCollection");
    const ImageLibrarySortingEnum         = require("ui/imageLibrary/ImageLibrarySortingEnum");
    const ImageLibrarySortingProvider     = require("ui/imageLibrary/ImageLibrarySortingProvider");
    const ImageLibrarySourceEnum          = require("shared/data/images/ImageLibrarySourceEnum");
    const UserCollection                  = require("ui/user/UserCollection");
    const Promise                         = require("shared/util/Promise");
    const ContentCollection               = require("data/ContentCollection");
    const PublishOptionsProvider          = require("editor/ui/experience/editor/PublishOptionsProvider");
    const PublishView                     = require("ui/publish/PublishView");
    const DashboardImageLibraryView       = require("ui/imageLibrary/DashboardImageLibraryView");
    const Permissions                     = require("shared/security/Permissions");
    const DashboardContentCollectionView  = require("ui/dashboard/DashboardContentCollectionView");
    const MatomoReportingView             = require("ui/reporting/MatomoReportingView");
    const ReportingModel                  = require("ui/reporting/MatomoReportingModel.js");


    // TODO: Remove this monstrosity

    const setReportingTabDisplay = () => {
        const matomoDashboardVisible = LoggedInUserState.get("integrations").isMatomoReportingDashboardLinkVisibleIntegrationsEnabled();
        if (matomoDashboardVisible) {
            if (!window._zmags_reporting_tab) {
                // Setting a global variable for use in DashboardNavigationView.tpl
                window._zmags_reporting_tab = true; // eslint-disable-line
                const reportingTab = document.getElementById("zmags-reporting-tab"); // This will ensure the tab becomes visible before templates are applied the second time.
                reportingTab.style.display = "inline-block";
            }
        }
    };

    // </monstrosity>

    /**
     * Represents a tab specific configuration.
     *
     * @class DashboardTabConfiguration
     */
    const DashboardTabConfiguration = ImprovedModel.extend("DashboardTabConfiguration", {

        defaults: {
            view: null,
            name: null,
            slug: null,
            path: "",
            collection: null,
            length: 0,
            reloadOptions: false,

            /**
             * Object describing the current context, if any. (Used when in the
             * project context - see ProjectDashboardController)
             *
             * @type {Object}
             */
            context: null
        },

        computed: {
            href: {
                deps: ["path", "context"],
                value(path, context) {
                    return isString(path) ? path : path.call(this, context);
                }
            }
        },

        initialize(attributes, options) {
            this.fetchOptions = options.fetchOptions;
            this.count = (options && options.count) || this.count;
        },

        invalidateLength() {
            const value = this.count();

            if (Promise.isPromise(value)) {
                return value.then((length) => {
                    this.set("length", length);
                });
            }

            this.set("length", value);

            return Promise.resolved(value);
        },

        count() {
            return 0;
        }
    });

    /**
     * Factory for creating a dashboard configuration.
     *
     * This abstraction is needed to support multiple dashboards, e.g. the project dashboard.
     *
     * @class DashboardConfigurationFactory
     *
     * @see ProjectDashboardConfiguration
     */
    return {
        /**
         * Creates the configuration, a an Enumeration of <tt>DashboardTabConfiguration</tt>'s. If no options are
         * passed, the default configuration is returned.
         *
         * @param  {Object} options Options to override the default configuration, see <tt>ProjectDashboardConfiguration</tt>.
         * @return {Enum}           Enumeration of <tt>DashboardTabConfiguration</tt>'s.
         */
        create(options) {
            const idField = {

                EXPERIENCES: new DashboardTabConfiguration({
                    view: DashboardContentCollectionView,
                    name: "Experiences",
                    slug: "experiences",
                    path: ""
                }, {
                    fetchOptions() {
                        setReportingTabDisplay();
                        const collection = new ContentCollection(this.get("context"));

                        return Promise.all([
                            collection.query(DashboardSorting.current().query),
                            // We need the list of users to correctly render "Created By"...
                            new UserCollection().fetch()
                        ])
                            .then((results) =>
                                ({
                                    collection,
                                    principal: LoggedIn.principal(),
                                    users: results[1],
                                    spectatorMode: Permissions.Privileges.CREATE.cannot()
                                }));
                    },
                    count() {
                        return ContentCollection.count(this.get("context"));
                    }
                }),

                IMAGES: new DashboardTabConfiguration({
                    view: DashboardImageLibraryView,
                    name: "Images",
                    slug: "images",
                    path: "images"
                }, {
                    fetchOptions() {
                        setReportingTabDisplay();
                        const imageCollection = new ImageCollection(extend({autoRemoval: true}, this.get("context")));
                        const imageViewedEventsEnabled = LoggedInUserState.get("companySettings").get("imageViewedEventsEnabled");

                        let creatorSorting = ImageLibrarySortingEnum.without(ImageLibrarySortingEnum.MOST_VIEWED);

                        if (imageViewedEventsEnabled) {
                            creatorSorting = ImageLibrarySortingEnum.all();
                        }

                        const imageLibrarySorting = ImageLibrarySortingProvider.provide({
                            sources: [
                                {source: ImageLibrarySourceEnum.CREATOR, sorting: creatorSorting}
                            ]
                        });

                        const imageTaggingEnabled = LoggedInUserState.get("integrations").imageRekognitionIntegrationsEnabled();

                        const inventoryEnabled = LoggedInUserState.get("integrations").isInventoryEnabled();

                        return imageCollection.query(imageLibrarySorting.getSelectedSorting().query)
                            .then(() =>
                                ({
                                    imageTaggingEnabled,
                                    inventoryEnabled,
                                    spectatorMode: Permissions.Privileges.CREATE.cannot(),
                                    collection: imageCollection,
                                    imageViewedEventsEnabled,
                                    imageLibrarySorting
                                }));
                    },
                    count() {
                        return ImageCollection.count(this.get("context"));
                    }
                }),

                PUBLISH: new DashboardTabConfiguration({
                    view: PublishView,
                    name: "Publish",
                    slug: "publish",
                    path: "publish",
                    // Signal that we wanna reload/fetch the PublishView options every time we enter the publish tab.
                    reloadOptions: true
                }, {
                    /**
                     * Fetch the channel configurations
                     * @returns {Promise} promise with information about the channels information
                     */
                    fetchOptions(options) {
                        setReportingTabDisplay();
                        return PublishOptionsProvider.provide(extend(options, {maxScenes: 0}))
                            .then((options) =>
                                extend(options, {
                                    spectatorMode: Permissions.Privileges.CREATE.cannot()
                                }));
                    },
                    count() {
                        return ChannelCollection.count(this.get("context"));
                    }
                }),
                REPORTING: new DashboardTabConfiguration({
                    view: MatomoReportingView,
                    name: "Reporting",
                    slug: "reporting",
                    path: "reporting",
                    reloadOptions: true
                }, {
                    /**
                     * Since this is not a collection, count() is filler
                     * Also fetchOptions hands the return from a promise to the view as an option
                     */
                    fetchOptions(options) {
                        setReportingTabDisplay();
                        const matomoModel = new ReportingModel();
                        switch (LoggedInUserState.get("location")) {
                            case LoggedInUserState.Location.REPORTING_SEGMENT:
                                return matomoModel.redirectToSegmentPage(options.partialViewOptions.segmentDimension);
                            case LoggedInUserState.Location.REPORTING:
                            default:
                                return matomoModel.getMatomoRedirect();
                        }
                    },
                    count() {
                        return 0;
                    }
                })
            };


            // TODO: Remove this after May 14, 2019
            const reportingCalloutEndDate = new Date("April 30, 2019").valueOf();

            if (Date.now() <= reportingCalloutEndDate) {
                idField.REPORTING.set("callout", "NEW");
            }

            const config = Enum.create("DashboardTabConfiguration", idField, {
                context(value) {
                    return this.each((tabConfig) => {
                        tabConfig.set("context", value);
                    });
                },
                fromSlug(slug) {
                    return this.each((tabConfig) => {
                        if (tabConfig.get("slug") === slug) {
                            return tabConfig;
                        }
                    });
                }
            });

            if (options) {
                each(options, (option, key) => {
                    const tabConfig = config[key];

                    tabConfig.set(option, {silent: true});
                });
            }

            return config;
        }
    };
});



// WEBPACK FOOTER //
// ./src/editor/ui/dashboard/DashboardConfigurationFactory.js